import React, { useState, useMemo } from 'react';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import { formatNomenclatureWithSize } from '@equip/utils';
import {
  Box,
  Button,
  Container,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridRowSelectionModel } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Print as PrintIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  useAllBarcodeMappings,
  useCreateBarcodeMapping,
  useDeleteBarcodeMapping,
  type BarcodeLabelRow,
} from '@/hooks/queries/barcodes';
import { entityService } from '@/services/EntityService';
import { BarcodeLabelPrintDialog } from '@/components/barcode/BarcodeLabelPrintDialog';
import {
  BarcodeLabelBulkPrintDialog,
  type BarcodeLabelBulkPrintItem,
} from '@/components/barcode/BarcodeLabelBulkPrintDialog';
import { LoadingSkeleton } from '@/components/shared/LoadingSkeleton';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import { useNotifications } from '@/stores/useNotificationStore';

export const BarcodeLabelsPage: React.FC = () => {
  const isViewer = useWriteGuard();
  const notifications = useNotifications();
  const { data: mappings, isLoading, error, refetch } = useAllBarcodeMappings();
  const createMapping = useCreateBarcodeMapping();
  const deleteMapping = useDeleteBarcodeMapping();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<BarcodeLabelRow | null>(null);
  const [printDialogMapping, setPrintDialogMapping] = useState<BarcodeLabelRow | null>(null);
  const [bulkPrintOpen, setBulkPrintOpen] = useState(false);

  // Create dialog state
  const [selectedCatalog, setSelectedCatalog] = useState<{ id: string; label: string } | null>(
    null,
  );
  const [selectedLocation, setSelectedLocation] = useState<{ id: string; label: string } | null>(
    null,
  );
  const [catalogs, setCatalogs] = useState<{ id: string; label: string }[]>([]);
  const [locations, setLocations] = useState<{ id: string; label: string }[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);

  const filteredMappings = useMemo(() => {
    if (!mappings) return [];
    if (!searchTerm.trim()) return mappings;
    const term = searchTerm.toLowerCase();
    return mappings.filter(
      (m) =>
        m.barcode_value.toLowerCase().includes(term) ||
        m.catalog_name.toLowerCase().includes(term) ||
        (m.nsn?.toLowerCase().includes(term) ?? false) ||
        (m.lin?.toLowerCase().includes(term) ?? false) ||
        (m.old_lin?.toLowerCase().includes(term) ?? false) ||
        m.location_name.toLowerCase().includes(term) ||
        m.location_path.toLowerCase().includes(term),
    );
  }, [mappings, searchTerm]);

  const selectedMappings = useMemo(() => {
    if (!filteredMappings.length || selectedRows.length === 0) return [];
    const selectedSet = new Set(selectedRows.map(String));
    return filteredMappings.filter((m) => selectedSet.has(m.id));
  }, [filteredMappings, selectedRows]);

  const bulkPrintItems: BarcodeLabelBulkPrintItem[] = useMemo(
    () =>
      selectedMappings.map((m) => ({
        id: m.id,
        barcode_value: m.barcode_value,
        location_name: m.location_name,
        location_path: m.location_path,
        catalog_name: m.catalog_name,
        lin: m.lin,
        old_lin: m.old_lin,
        nsn: m.nsn,
        color: m.color,
        unit_of_issue: m.unit_of_issue,
        part_number: m.part_number,
        warehouse_location_name: m.warehouse_location_name,
        storefront_location_name: m.storefront_location_name,
      })),
    [selectedMappings],
  );

  const fetchCatalogs = async (query = '') => {
    setLoadingCatalogs(true);
    try {
      const results = query
        ? await entityService.searchEntities(query, 'catalog')
        : await entityService.list({ type: 'catalog', limit: 50 });
      setCatalogs(results.map((c) => ({
        id: c.id,
        label: formatNomenclatureWithSize(
          c.display_name || 'Unnamed',
          (c.entity_data as Record<string, unknown> | undefined)?.size as string | undefined,
        ),
      })));
    } catch (err) {
      console.error('Failed to fetch catalogs', err);
    } finally {
      setLoadingCatalogs(false);
    }
  };

  const fetchLocations = async (query = '') => {
    setLoadingLocations(true);
    try {
      const results = query
        ? await entityService.searchEntities(query, 'location')
        : await entityService.listAll({ type: 'location' });
      setLocations(results.map((l) => ({ id: l.id, label: l.display_name || 'Unnamed' })));
    } catch (err) {
      console.error('Failed to fetch locations', err);
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleCreateLabel = async () => {
    if (!selectedCatalog || !selectedLocation) return;
    try {
      await createMapping.mutateAsync({
        catalogId: selectedCatalog.id,
        locationId: selectedLocation.id,
      });
      setCreateDialogOpen(false);
      setSelectedCatalog(null);
      setSelectedLocation(null);
    } catch (err) {
      console.error('Failed to create mapping', err);
    }
  };

  const handleDeleteMapping = (row: BarcodeLabelRow) => {
    setDeleteTarget(row);
  };

  const handleConfirmDeleteMapping = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMapping.mutateAsync({
        mappingId: deleteTarget.id,
      });
      notifications.success('Barcode label deleted', `${deleteTarget.catalog_name} mapping removed.`);
      setDeleteTarget(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete barcode label.';
      notifications.error('Delete failed', message);
      console.error('Failed to delete mapping', err);
    }
  };

  const handleBulkPrint = () => {
    if (selectedRows.length === 0) return;
    setBulkPrintOpen(true);
  };

  const columns: GridColDef<BarcodeLabelRow>[] = [
    {
      field: 'lin',
      headerName: 'LIN',
      width: 100,
    },
    {
      field: 'nsn',
      headerName: 'NSN',
      width: 150,
    },
    {
      field: 'catalog_name',
      headerName: 'Nomenclature',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'location_name',
      headerName: 'Location',
      width: 180,
    },
    {
      field: 'location_path',
      headerName: 'Path',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            {params.value || '—'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'barcode_value',
      headerName: 'Barcode',
      width: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Action',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent="center"
          sx={{ height: '100%', width: '100%' }}
        >
          <Tooltip title="Print Label">
            <IconButton
              size="small"
              color="primary"
              onClick={() => setPrintDialogMapping(params.row)}
            >
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!isViewer && (
            <Tooltip title="Delete">
              <IconButton size="small" color="error" onClick={() => handleDeleteMapping(params.row)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  if (isLoading) {
    return <LoadingSkeleton variant="grid" count={10} />;
  }

  if (error) {
    return (
      <Container maxWidth={false} sx={{ mt: 2 }}>
        <Alert severity="error" action={<Button onClick={() => refetch()}>Retry</Button>}>
          Failed to load barcode labels.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Barcode Labels
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage barcode labels for quick kiosk scanning, shelf identification, and warehouse box
          tracking.
        </Typography>
      </Box>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          placeholder="Search labels..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          disabled={selectedRows.length === 0}
          onClick={handleBulkPrint}
        >
          Print Selected ({selectedRows.length})
        </Button>
        {!isViewer && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setCreateDialogOpen(true);
              fetchCatalogs();
              fetchLocations();
            }}
          >
            Create Label
          </Button>
        )}
        <Tooltip title="Refresh">
          <IconButton onClick={() => refetch()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Box sx={{ height: 600 }}>
        <DataGrid
          rows={filteredMappings}
          columns={columns}
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectedRows}
          onRowSelectionModelChange={setSelectedRows}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          sx={{
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
          }}
        />
      </Box>

      {/* Create Label Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create Barcode Label</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Autocomplete
              options={catalogs}
              loading={loadingCatalogs}
              value={selectedCatalog}
              onInputChange={(_, value) => fetchCatalogs(value)}
              onChange={(_, value) => setSelectedCatalog(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Catalog Item"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCatalogs ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Autocomplete
              options={locations}
              loading={loadingLocations}
              value={selectedLocation}
              onInputChange={(_, value) => fetchLocations(value)}
              onChange={(_, value) => setSelectedLocation(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Shelf Location"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingLocations ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <ViewerGuardedButton
            onClick={handleCreateLabel}
            variant="contained"
            disabled={!selectedCatalog || !selectedLocation || createMapping.isPending}
          >
            Create Label
          </ViewerGuardedButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteTarget}
        onClose={deleteMapping.isPending ? undefined : () => setDeleteTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete Barcode Label</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the barcode label for{' '}
            <strong>{deleteTarget?.catalog_name}</strong> at{' '}
            <strong>{deleteTarget?.location_name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={deleteMapping.isPending} onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <ViewerGuardedButton
            color="error"
            variant="contained"
            disabled={deleteMapping.isPending}
            onClick={handleConfirmDeleteMapping}
          >
            {deleteMapping.isPending ? 'Deleting…' : 'Delete'}
          </ViewerGuardedButton>
        </DialogActions>
      </Dialog>

      {/* Print Dialog */}
      {printDialogMapping && (
        <BarcodeLabelPrintDialog
          open={!!printDialogMapping}
          onClose={() => setPrintDialogMapping(null)}
          mapping={{
            barcode_value: printDialogMapping.barcode_value,
            location_name: printDialogMapping.location_name,
            location_path: printDialogMapping.location_path,
            catalog_name: printDialogMapping.catalog_name,
            lin: printDialogMapping.lin,
            old_lin: printDialogMapping.old_lin,
            nsn: printDialogMapping.nsn,
            color: printDialogMapping.color,
            unit_of_issue: printDialogMapping.unit_of_issue,
            part_number: printDialogMapping.part_number,
            warehouse_location_name: printDialogMapping.warehouse_location_name,
            storefront_location_name: printDialogMapping.storefront_location_name,
          }}
        />
      )}

      <BarcodeLabelBulkPrintDialog
        open={bulkPrintOpen}
        onClose={() => setBulkPrintOpen(false)}
        items={bulkPrintItems}
      />
    </Container>
  );
};
